<script setup lang="ts">
  import { ref, onMounted, computed, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import { useModal } from 'vue-final-modal';
  import { storeToRefs } from 'pinia';
  import { Navbar } from '@/shared/ui';
  import { Footer } from '@/shared/ui/footer';
  import { NotificationCenterModal } from '@/shared/ui/notification-center';
  import { Sidebar } from '@/widgets/sidebar';
  import { useNotificationStore } from '@/entities/Notification';
  import { useFileUploadStore } from '@/features/Document';
  import { useUserStore, userApi } from '@/entities/User';
  import { useBranchLogo } from '@/entities/Branch';
  import { usePermission } from '@/entities/Permission';
  import { DocumentUploadModal } from '@/widgets/document-upload-modal';
  import { Breadcrumb } from '@/shared/ui';
  import { MENU_ITEMS } from '@/widgets/sidebar';
  import { CaslAction, CaslSubject } from '@/shared/lib/casl';
  import { useReminderHistoryStore } from '@/entities/ReminderHistory';
  import { useAuthStore } from '@/entities/Session';
  import { useCustomToast } from '@/shared/lib';

  const route = useRoute();

  const ROUTES_WITH_OWN_BREADCRUMB = new Set([
    'FilteredList',
    'QualityRecordMatrix',
    'ModuleMatrixRevision',
  ]);
  const isCollapsed = ref(false);
  const toast = useCustomToast();

  // Store management
  const notificationStore = useNotificationStore();
  const { fetchApprovalCount, fetchVerificationCount } = notificationStore;
  const { approvals, verifications } = storeToRefs(notificationStore);
  const fileUploadStore = useFileUploadStore();
  const { deleteDocuments } = fileUploadStore;
  const reminderHistoryStore = useReminderHistoryStore();
  const {
    unreadNoticeCount,
    recentNotices,
    isSummaryLoading: isNoticesLoading,
    processingNoticeId,
  } = storeToRefs(reminderHistoryStore);
  const { loadUnreadNoticeCount, loadNoticeSummary, acknowledgeNotice, clearNotices } =
    reminderHistoryStore;

  // Auth and User stores for LogoutButton
  const userStore = useUserStore();
  const { clearAllLogoCache } = useBranchLogo();
  const { can } = usePermission();
  const canCheck = (action: string, subject: string) =>
    can(action as CaslAction, subject as CaslSubject).value;

  // O botão Admin exige read:Admin — permissão base de acesso ao painel administrativo.
  // As demais permissões de Admin (managePermissions, manageBranches, ...) são concedidas
  // sempre junto de read:Admin, então basta verificar read:Admin aqui.
  const adminButtonPermissions: { action: CaslAction; subject: CaslSubject }[] = [
    { action: 'read', subject: 'Admin' },
  ];

  // Get user data with proper null handling
  const userName = computed(() => userStore.name || '');
  const userEmail = computed(() => userStore.user?.email || '');

  const isLoading = ref(true);
  const isNotificationModalOpen = ref(false);

  // Upload modal management
  const { open: openUpload, close: closeUpload } = useModal({
    component: DocumentUploadModal,
    attrs: {
      // @ts-ignore - vue-final-modal props not typed in component attrs
      clickToClose: false,
      escToClose: false,
      title: 'Envio de documentos',
      onClose(documentIds: number[] | undefined) {
        if (documentIds) {
          deleteDocuments(documentIds);
        }
        fileUploadStore.files = [];
        fileUploadStore.hasFiles = false;
        fileUploadStore.filesLoaded = false;
        fileUploadStore.uploadedDocumentIds = [];
        fileUploadStore.fileToDocumentIdMap.clear();
        closeUpload();
      },
      async onUploadSuccess() {
        await fetchApprovalCount();
      },
    },
  });

  const authStore = useAuthStore();
  const { selectedBranchId } = storeToRefs(authStore);

  const refreshPersistedUser = async () => {
    try {
      const response = await userApi.getCurrentUser();
      if (response.data.success) {
        userStore.setUser(response.data.data);
        clearAllLogoCache();
      }
    } catch (error) {
      console.warn('Could not refresh the persisted user:', error);
    }
  };

  // Fetch counts on mount
  onMounted(async () => {
    void refreshPersistedUser();
    try {
      await Promise.all([fetchApprovalCount(), fetchVerificationCount()]);
      await loadUnreadNoticeCount();
    } catch {
      // Silently handle counter fetch errors - non-critical UI data
    } finally {
      isLoading.value = false;
    }
  });

  watch(selectedBranchId, (newId, oldId) => {
    if (newId !== null && newId !== oldId) {
      clearNotices();
      void loadUnreadNoticeCount();
    }
  });

  const toggleSidebar = () => {
    isCollapsed.value = !isCollapsed.value;
  };

  const handleOpenUpload = () => {
    openUpload();
  };

  const handleOpenNotification = () => {
    isNotificationModalOpen.value = true;
    void loadNoticeSummary();
  };

  const handleCloseNotification = () => {
    isNotificationModalOpen.value = false;
  };

  const handleAcknowledgeNotice = async (noticeId: number) => {
    if (processingNoticeId.value !== null) {
      return;
    }

    try {
      const didAcknowledge = await acknowledgeNotice(noticeId);

      if (!didAcknowledge) {
        return;
      }
    } catch {
      toast.showErrorToast({
        title: 'Erro ao confirmar ciência do aviso',
        message: 'Tente novamente em instantes.',
        type: 'error',
      });
    }
  };
</script>

<template>
  <div class="flex h-screen overflow-hidden">
    <div
      :class="[
        isCollapsed ? 'w-16' : 'w-60',
        'border-r border-[var(--theme-secondary-ink)] transition-all duration-300 flex-shrink-0 bg-[var(--theme-primary)]',
        'h-screen fixed left-0 top-0 overflow-y-auto',
      ]"
    >
      <Sidebar
        :is-collapsed="isCollapsed"
        :user-name="userName"
        :user-email="userEmail"
        :menu-items="MENU_ITEMS"
        :can-check="canCheck"
        :admin-permissions="adminButtonPermissions"
        admin-match-mode="all"
        @expand="toggleSidebar"
      />
    </div>

    <div
      :class="[
        isCollapsed ? 'ml-16' : 'ml-60',
        'w-full flex flex-col transition-all duration-300 overflow-auto relative',
      ]"
    >
      <div class="h-20 border-b border-[var(--theme-secondary-ink)] text-[var(--theme-text)] flex">
        <Navbar
          :toggle-sidebar="toggleSidebar"
          :is-collapsed="isCollapsed"
          :approval-count="approvals"
          :verification-count="verifications"
          :is-loading="isLoading"
          :unread-notice-count="unreadNoticeCount"
          :can-check="canCheck"
          @open-upload="handleOpenUpload"
          @open-notification="handleOpenNotification"
        />
      </div>
      <Breadcrumb
        v-if="!ROUTES_WITH_OWN_BREADCRUMB.has(String(route.name))"
        class="pt-4 pr-6"
      />
      <main class="flex-1 overflow-auto bg-white-02">
        <router-view v-slot="{ Component, route: viewRoute }">
          <component
            :is="Component"
            :key="viewRoute.meta.remountOnParamsChange ? viewRoute.path : undefined"
          />
        </router-view>
      </main>

      <NotificationCenterModal
        v-if="isNotificationModalOpen"
        title="Atualizações"
        :notifications="recentNotices"
        :is-loading="isNoticesLoading"
        :processing-notice-id="processingNoticeId"
        @close="handleCloseNotification"
        @acknowledge="handleAcknowledgeNotice"
      />

      <Footer />
    </div>
  </div>
</template>
